(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const heroControllers = new WeakMap();

  class HeroControls {
    constructor(hero) {
      this.hero = hero;
      this.abort = new AbortController();
      this.pausedByUser = reducedMotion.matches;
      this.inView = false;
      this.videos = [...hero.querySelectorAll('.hero-media video')];
      this.toggle = hero.querySelector('[data-hero-video-toggle]');
      this.switcher = hero.querySelector('[data-background-switch]');
      this.switchLabel = this.switcher?.textContent;
      const on = (target, event, callback) => target?.addEventListener(event, callback, { signal: this.abort.signal });
      this.videos.forEach(video => {
        video.muted = true;
        video.playsInline = true;
        on(video, 'playing', () => {
          if (video !== this.activeVideo() || this.pausedByUser || !this.inView) {
            video.pause();
            return;
          }
          const wrapper = video.closest('[data-video-fallback]');
          wrapper?.classList.add('is-video-ready');
          wrapper?.classList.remove('is-video-fallback');
          this.update();
        });
        on(video, 'pause', () => this.update());
        on(video, 'error', () => this.fallback(video));
      });
      on(this.toggle, 'click', () => {
        const video = this.activeVideo();
        if (!video) return;
        this.pausedByUser = !video.paused;
        if (this.pausedByUser) video.pause();
        else this.play(video);
        this.update();
      });
      if (this.switcher) this.switcher.hidden = false;
      on(this.switcher, 'click', () => {
        const secondary = this.hero.classList.toggle('is-secondary');
        this.switcher.setAttribute('aria-pressed', String(secondary));
        this.switcher.textContent = secondary ? 'PRIMARY VISUAL' : this.switchLabel;
        this.sync();
      });
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.sync();
      }, { threshold: 0.15 });
      this.observer.observe(hero);
      this.update();
    }
    activeVideo() {
      return this.hero.querySelector(this.hero.classList.contains('is-secondary') ? '.hero-media--secondary video' : '.hero-media--primary video');
    }
    fallback(video) {
      const wrapper = video.closest('[data-video-fallback]');
      if (!wrapper?.classList.contains('is-video-ready')) wrapper?.classList.add('is-video-fallback');
      this.update();
    }
    play(video) {
      if (!video) return;
      video.play()?.then(() => {
        if (!video.paused) {
          const wrapper = video.closest('[data-video-fallback]');
          wrapper?.classList.add('is-video-ready');
          wrapper?.classList.remove('is-video-fallback');
        }
        this.update();
      }).catch(() => this.fallback(video));
    }
    sync() {
      const active = this.activeVideo();
      this.videos.forEach(video => {
        if (video === active && this.inView && !this.pausedByUser) this.play(video);
        else video.pause();
      });
      this.update();
    }
    update() {
      const active = this.activeVideo();
      const playing = active && !active.paused;
      if (this.toggle) {
        this.toggle.hidden = !active;
        this.toggle.textContent = playing ? 'PAUSE VIDEO' : 'PLAY VIDEO';
        this.toggle.setAttribute('aria-pressed', String(Boolean(playing)));
      }
      this.hero.classList.toggle('is-user-paused', this.pausedByUser);
    }
    destroy() { this.abort.abort(); this.observer.disconnect(); }
  }

  class CommunityProducts extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.tabs = [...this.querySelectorAll('[data-community-tab]')];
      this.panels = this.tabs.map(tab => this.querySelector(`[id="${tab.hash.slice(1)}"]`));
      if (!this.tabs.length) {
        if (!window.Shopify?.designMode) this.hidden = true;
        return;
      }
      this.hidden = false;
      this.querySelector('[data-community-tabs]').setAttribute('role', 'tablist');
      this.classList.add('is-enhanced');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[index].id);
        this.panels[index].setAttribute('role', 'tabpanel');
        tab.addEventListener('click', event => { event.preventDefault(); this.select(index); }, { signal: this.abort.signal });
        tab.addEventListener('keydown', event => {
          const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
          if (!direction && event.key !== 'Home' && event.key !== 'End' && event.key !== ' ') return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + direction + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        }, { signal: this.abort.signal });
      });
      this.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel.dataset.blockId === event.detail?.blockId);
        if (index !== -1) this.select(index);
      }, { signal: this.abort.signal });
      this.select(0);
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  if (!customElements.get('x-community-products')) customElements.define('x-community-products', CommunityProducts);

  const init = (root = document) => {
    root.querySelectorAll('.hero-section').forEach(hero => {
      heroControllers.get(hero)?.destroy();
      heroControllers.set(hero, new HeroControls(hero));
    });
    // Keep poster fallback working for the optional intro, independently of the hero.
    root.querySelectorAll('.intro-gate [data-video-fallback] video').forEach(video => {
      const wrapper = video.closest('[data-video-fallback]');
      wrapper.classList.add('is-video-fallback');
      const ready = () => { wrapper.classList.add('is-video-ready'); wrapper.classList.remove('is-video-fallback'); };
      video.addEventListener('playing', ready, { once: true });
      if (!video.paused && video.readyState >= 2) ready();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.hero-section').forEach(hero => heroControllers.get(hero)?.destroy()));
})();
